import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Package, Plus, Trash2, Search, Download, Send, AlertTriangle,
  Archive, Gift, PlusCircle, Edit, Image as ImageIcon
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'drinkware', label: 'Drinkware' },
  { value: 'writing', label: 'Writing' },
  { value: 'apparel', label: 'Apparel' },
  { value: 'print_materials', label: 'Print Materials' },
  { value: 'bags', label: 'Bags' },
  { value: 'tech', label: 'Tech' },
  { value: 'other', label: 'Other' },
];

export default function MarketingSwag() {
  const { hasRole } = useAuth();
  const perms = useEffectivePermissions();
  // Engine-aware write gate. View-only HR overrides should NOT see write actions.
  const canAdd = perms.can('marketing', 'add') || hasRole('admin', 'marketing');
  const canEdit = perms.can('marketing', 'edit') || hasRole('admin', 'marketing');
  const canDelete = perms.can('marketing', 'delete') || hasRole('admin', 'marketing');
  const canExport = perms.can('marketing', 'export') || hasRole('admin', 'marketing');
  const [items, setItems] = useState([]);
  const [kits, setKits] = useState([]);
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Dialogs
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddStock, setShowAddStock] = useState(null);
  const [showSend, setShowSend] = useState(false);
  const [showKit, setShowKit] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingKit, setEditingKit] = useState(null);

  // Forms
  const [itemForm, setItemForm] = useState({ name: '', description: '', category: 'other', quantity: 0, low_stock_threshold: 10 });
  const [stockForm, setStockForm] = useState({ quantity: 0, notes: '', vendor_reference: '', date_received: '', mode: 'add' });
  const [sendType, setSendType] = useState('individual'); // 'individual' or 'bulk'
  const [sendForm, setSendForm] = useState({ recipient_name: '', event_or_campaign: '', date_sent: '', notes: '', items: [], kit_template_id: '', kit_name: '', kit_quantity: 1 });
  const [kitForm, setKitForm] = useState({ name: '', description: '', items: [] });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [itemsRes, kitsRes, sendsRes] = await Promise.all([
        api.get('/swag/items'),
        api.get('/swag/kits'),
        api.get('/swag/sends'),
      ]);
      setItems(itemsRes.data);
      setKits(kitsRes.data);
      setSends(sendsRes.data);
    } catch (error) {
      console.error('Failed to load swag data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ===== ITEM CRUD =====
  const handleSaveItem = async () => {
    if (!itemForm.name) { toast.error('Item name is required'); return; }
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/swag/items/${editingItem.id}`, {
          name: itemForm.name, description: itemForm.description,
          category: itemForm.category, low_stock_threshold: itemForm.low_stock_threshold
        });
        toast.success('Item updated');
      } else {
        await api.post('/swag/items', itemForm);
        toast.success('Item created');
      }
      setShowAddItem(false);
      setEditingItem(null);
      setItemForm({ name: '', description: '', category: 'other', quantity: 0, low_stock_threshold: 10 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally { setSaving(false); }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await api.delete(`/swag/items/${item.id}`);
      toast.success('Item deleted');
      loadData();
    } catch (error) { toast.error('Failed to delete item'); }
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({ name: item.name, description: item.description || '', category: item.category, quantity: item.quantity, low_stock_threshold: item.low_stock_threshold });
    setShowAddItem(true);
  };

  // ===== SEND (Unified: Individual + Bulk) =====
  const openSendDialog = (kit = null) => {
    if (kit) {
      setSendForm({ recipient_name: '', event_or_campaign: '', date_sent: '', notes: '', items: kit.items.map(i => ({ item_id: i.item_id, item_name: i.item_name, quantity: i.quantity })), kit_template_id: kit.id, kit_name: kit.name, kit_quantity: 1 });
    } else {
      setSendForm({ recipient_name: '', event_or_campaign: '', date_sent: '', notes: '', items: [], kit_template_id: '', kit_name: '', kit_quantity: 1 });
    }
    setSendType('individual');
    setShowSend(true);
  };

  const handleSend = async () => {
    if (sendType === 'individual' && !sendForm.recipient_name) { toast.error('Recipient name is required for individual sends'); return; }
    if (sendType === 'bulk' && sendForm.kit_quantity <= 0) { toast.error('Quantity must be at least 1'); return; }
    if (sendForm.items.length === 0) { toast.error('Add at least one item to send'); return; }
    setSaving(true);
    try {
      if (sendType === 'bulk') {
        const res = await api.post('/swag/bulk-send', {
          kit_template_id: sendForm.kit_template_id,
          kit_name: sendForm.kit_name,
          kit_quantity: sendForm.kit_quantity,
          event_or_campaign: sendForm.event_or_campaign,
          date_sent: sendForm.date_sent,
          recipient_name: sendForm.recipient_name,
          notes: sendForm.notes,
          items: sendForm.items
        });
        if (res.data.blocked) {
          res.data.shortfalls?.forEach(s => toast.error(s));
          res.data.warnings?.forEach(w => toast.warning(w));
        } else {
          if (res.data.warnings?.length > 0) res.data.warnings.forEach(w => toast.warning(w));
          toast.success(res.data.message);
          setShowSend(false);
          loadData();
        }
      } else {
        const res = await api.post('/swag/send', {
          recipient_name: sendForm.recipient_name,
          event_or_campaign: sendForm.event_or_campaign,
          date_sent: sendForm.date_sent,
          notes: sendForm.notes,
          items: sendForm.items,
          kit_template_id: sendForm.kit_template_id
        });
        if (res.data.warnings?.length > 0) res.data.warnings.forEach(w => toast.warning(w));
        toast.success('Items sent!');
        setShowSend(false);
        loadData();
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to send items'); }
    finally { setSaving(false); }
  };

  const loadKitIntoSend = (kitId) => {
    const kit = kits.find(k => k.id === kitId);
    if (kit) {
      setSendForm({ ...sendForm, items: kit.items.map(i => ({ item_id: i.item_id, item_name: i.item_name, quantity: i.quantity })), kit_template_id: kit.id, kit_name: kit.name });
    } else if (kitId === 'none') {
      setSendForm({ ...sendForm, items: [], kit_template_id: '', kit_name: '' });
    }
  };
  const handleAddStock = async () => {
    if (stockForm.quantity === 0) { toast.error('Quantity cannot be zero'); return; }
    if (stockForm.mode === 'add' && stockForm.quantity <= 0) { toast.error('Quantity must be positive when adding stock'); return; }
    setSaving(true);
    try {
      const endpoint = stockForm.mode === 'adjust'
        ? `/swag/items/${showAddStock.id}/adjust-stock`
        : `/swag/items/${showAddStock.id}/add-stock`;
      const payload = { quantity: stockForm.quantity, notes: stockForm.notes, vendor_reference: stockForm.vendor_reference, date_received: stockForm.date_received };
      await api.post(endpoint, payload);
      toast.success(stockForm.mode === 'adjust' ? `Adjusted by ${stockForm.quantity} units` : `Added ${stockForm.quantity} units`);
      setShowAddStock(null);
      setStockForm({ quantity: 0, notes: '', vendor_reference: '', date_received: '', mode: 'add' });
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update stock'); }
    finally { setSaving(false); }
  };

  // ===== IMAGE UPLOAD =====
  const handleImageUpload = async (itemId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/swag/items/${itemId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Image uploaded');
      loadData();
    } catch (error) { toast.error('Failed to upload image'); }
  };

  // ===== KITS =====
  const handleSaveKit = async () => {
    if (!kitForm.name || kitForm.items.length === 0) { toast.error('Kit needs a name and at least one item'); return; }
    setSaving(true);
    try {
      const payload = { name: kitForm.name, description: kitForm.description, items: kitForm.items.map(i => ({ item_id: i.item_id, quantity: i.quantity })) };
      if (editingKit) {
        await api.put(`/swag/kits/${editingKit.id}`, payload);
        toast.success('Kit updated');
      } else {
        await api.post('/swag/kits', payload);
        toast.success('Kit created');
      }
      setShowKit(false);
      setEditingKit(null);
      setKitForm({ name: '', description: '', items: [] });
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save kit'); }
    finally { setSaving(false); }
  };

  const handleDeleteKit = async (kit) => {
    if (!window.confirm(`Delete kit "${kit.name}"?`)) return;
    try { await api.delete(`/swag/kits/${kit.id}`); toast.success('Kit deleted'); loadData(); }
    catch { toast.error('Failed to delete kit'); }
  };

  const openEditKit = (kit) => {
    setEditingKit(kit);
    setKitForm({ name: kit.name, description: kit.description || '', items: kit.items.map(i => ({ item_id: i.item_id, item_name: i.item_name, quantity: i.quantity })) });
    setShowKit(true);
  };

  // ===== EXPORT =====
  const handleExportSends = async () => {
    try {
      const res = await api.get('/swag/sends/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'swag_send_history.csv'; a.click();
    } catch { toast.error('Export failed'); }
  };

  const handleExportInventory = async () => {
    try {
      const res = await api.get('/swag/inventory/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'swag_inventory.csv'; a.click();
    } catch { toast.error('Export failed'); }
  };

  // ===== FILTERS =====
  const filteredItems = items.filter(item => {
    const matchSearch = item.name.toLowerCase().includes(search.toLowerCase()) || (item.description || '').toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === 'all' || item.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const lowStockItems = items.filter(i => i.quantity <= i.low_stock_threshold);
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <div className="space-y-6" data-testid="marketing-swag-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Marketing Swag & Materials</h1>
          <p className="text-slate-500 mt-1">Track swag inventory, build kits, and log sends</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {canAdd && (
            <Button variant="outline" onClick={() => openSendDialog()} data-testid="send-swag-btn">
              <Send className="w-4 h-4 mr-2" /> Send Kit
            </Button>
          )}
          {canAdd && (
            <Button onClick={() => { setEditingItem(null); setItemForm({ name: '', description: '', category: 'other', quantity: 0, low_stock_threshold: 10 }); setShowAddItem(true); }} data-testid="add-swag-item-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Item
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Package className="w-5 h-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold" data-testid="stat-total-items">{items.length}</p><p className="text-sm text-slate-500">Item Types</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Archive className="w-5 h-5 text-emerald-600" /></div>
          <div><p className="text-2xl font-bold text-emerald-600" data-testid="stat-total-qty">{totalItems}</p><p className="text-sm text-slate-500">Total Units</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><Gift className="w-5 h-5 text-purple-600" /></div>
          <div><p className="text-2xl font-bold text-purple-600" data-testid="stat-kits">{kits.length}</p><p className="text-sm text-slate-500">Kit Templates</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${lowStockItems.length > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
            <AlertTriangle className={`w-5 h-5 ${lowStockItems.length > 0 ? 'text-red-600' : 'text-slate-400'}`} />
          </div>
          <div><p className={`text-2xl font-bold ${lowStockItems.length > 0 ? 'text-red-600' : ''}`} data-testid="stat-low-stock">{lowStockItems.length}</p><p className="text-sm text-slate-500">Low Stock</p></div>
        </CardContent></Card>
      </div>

      {/* Low Stock Alert Banner */}
      {lowStockItems.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="low-stock-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="font-medium text-red-800">Low Stock Alert</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStockItems.map(i => (
              <Badge key={i.id} variant="destructive" className="text-xs">{i.name}: {i.quantity} left</Badge>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="inventory" className="space-y-4">
        <TabsList>
          <TabsTrigger value="inventory">Inventory ({items.length})</TabsTrigger>
          <TabsTrigger value="kits">Kit Templates ({kits.length})</TabsTrigger>
          <TabsTrigger value="history">Send History ({sends.length})</TabsTrigger>
        </TabsList>

        {/* ===== INVENTORY TAB ===== */}
        <TabsContent value="inventory">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search items..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="swag-search" />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="swag-category-filter">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={handleExportInventory} data-testid="export-swag-inventory-btn" disabled={!canExport}>
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </div>

              {loading ? (
                <div className="p-8 space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-12 skeleton-pulse rounded" />)}</div>
              ) : filteredItems.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Threshold</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-40">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map(item => (
                      <TableRow key={item.id} data-testid={`swag-item-row-${item.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {item.image_id ? (
                              <img src={`${API_URL}/api/swag/items/${item.id}/image`} alt="" className="w-10 h-10 rounded object-cover border" />
                            ) : (
                              <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center"><Package className="w-5 h-5 text-slate-400" /></div>
                            )}
                            <div>
                              <p className="font-medium">{item.name}</p>
                              {item.description && <p className="text-xs text-slate-500 truncate max-w-[200px]">{item.description}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{item.category?.replace('_', ' ')}</Badge></TableCell>
                        <TableCell className="text-right font-bold">{item.quantity}</TableCell>
                        <TableCell className="text-right text-slate-500">{item.low_stock_threshold}</TableCell>
                        <TableCell>
                          {item.quantity <= item.low_stock_threshold ? (
                            <Badge variant="destructive">Low Stock</Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">In Stock</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {canEdit && (
                              <Button variant="ghost" size="sm" onClick={() => { setShowAddStock(item); setStockForm({ quantity: 0, notes: '', vendor_reference: '', date_received: '', mode: 'add' }); }} data-testid={`add-stock-${item.id}`}>
                                <PlusCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {canEdit && (
                              <Button variant="ghost" size="sm" onClick={() => openEditItem(item)} data-testid={`edit-item-${item.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {canEdit && (
                              <label className="cursor-pointer">
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) handleImageUpload(item.id, e.target.files[0]); }} />
                                <Button variant="ghost" size="sm" asChild><span><ImageIcon className="w-4 h-4" /></span></Button>
                              </label>
                            )}
                            {canDelete && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item)} data-testid={`delete-item-${item.id}`}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                            {!canEdit && !canDelete && (
                              <span className="text-xs text-slate-400 italic">View only</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No swag items yet</p>
                  <p className="text-sm">Click "Add Item" to get started</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== KITS TAB ===== */}
        <TabsContent value="kits">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div><CardTitle>Kit Templates</CardTitle><CardDescription>Predefined swag bags and welcome kits</CardDescription></div>
                {canAdd && (
                  <Button onClick={() => { setEditingKit(null); setKitForm({ name: '', description: '', items: [] }); setShowKit(true); }} data-testid="add-kit-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Kit Template
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {kits.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {kits.map(kit => (
                    <Card key={kit.id} className="border" data-testid={`kit-card-${kit.id}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold">{kit.name}</h3>
                          <div className="flex gap-1">
                            {canEdit && <Button variant="ghost" size="sm" onClick={() => openEditKit(kit)}><Edit className="w-3 h-3" /></Button>}
                            {canDelete && <Button variant="ghost" size="sm" onClick={() => handleDeleteKit(kit)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                          </div>
                        </div>
                        {kit.description && <p className="text-xs text-slate-500">{kit.description}</p>}
                        <div className="space-y-1">
                          {kit.items?.map((ki, idx) => (
                            <div key={idx} className="text-sm flex justify-between">
                              <span>{ki.item_name}</span>
                              <span className="text-slate-500">x{ki.quantity}</span>
                            </div>
                          ))}
                        </div>
                        {canAdd && (
                          <Button variant="outline" size="sm" className="w-full" onClick={() => openSendDialog(kit)} data-testid={`send-kit-${kit.id}`}>
                            <Send className="w-3 h-3 mr-1" /> Send This Kit
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Gift className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No kit templates yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== SEND HISTORY TAB ===== */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div><CardTitle>Send History</CardTitle><CardDescription>Log of all items and kits sent</CardDescription></div>
                <Button variant="outline" onClick={handleExportSends} data-testid="export-sends-btn">
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {sends.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Recipient / Campaign</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Sent By</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sends.map(send => (
                      <TableRow key={send.id}>
                        <TableCell>
                          {send.type === 'bulk' ? (
                            <Badge className="bg-purple-100 text-purple-800 text-xs">Bulk ({send.kit_quantity})</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Individual</Badge>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{send.date_sent || formatDate(send.created_at)}</TableCell>
                        <TableCell>
                          <p className="font-medium">{send.recipient_name || send.kit_name || '-'}</p>
                          {send.event_or_campaign && <p className="text-xs text-slate-500">{send.event_or_campaign}</p>}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            {send.items?.map((si, i) => (
                              <p key={i} className="text-sm">{si.item_name} <span className="text-slate-500">x{send.type === 'bulk' ? si.total_deducted : si.quantity}</span></p>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{send.sent_by_name}</TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">{send.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500"><Send className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p>No sends recorded yet</p></div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== ADD/EDIT ITEM DIALOG ===== */}
      <Dialog open={showAddItem} onOpenChange={(open) => { setShowAddItem(open); if (!open) setEditingItem(null); }}>
        <DialogContent data-testid="add-item-dialog">
          <DialogHeader><DialogTitle>{editingItem ? 'Edit Item' : 'Add Swag Item'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Name *</Label><Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} placeholder="e.g., TriCorps Logo Pen" data-testid="swag-item-name" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} placeholder="Brief description..." data-testid="swag-item-desc" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Category</Label>
                <Select value={itemForm.category} onValueChange={(v) => setItemForm({ ...itemForm, category: v })}>
                  <SelectTrigger data-testid="swag-item-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {!editingItem && (
                <div className="space-y-2"><Label>Initial Quantity</Label><Input type="number" min="0" value={itemForm.quantity} onChange={(e) => setItemForm({ ...itemForm, quantity: parseInt(e.target.value) || 0 })} data-testid="swag-item-qty" /></div>
              )}
            </div>
            <div className="space-y-2"><Label>Low Stock Threshold</Label><Input type="number" min="0" value={itemForm.low_stock_threshold} onChange={(e) => setItemForm({ ...itemForm, low_stock_threshold: parseInt(e.target.value) || 0 })} data-testid="swag-item-threshold" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
            <Button onClick={handleSaveItem} disabled={saving} data-testid="save-swag-item-btn">{saving ? 'Saving...' : editingItem ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== ADD / ADJUST STOCK DIALOG ===== */}
      <Dialog open={!!showAddStock} onOpenChange={() => setShowAddStock(null)}>
        <DialogContent data-testid="add-stock-dialog">
          <DialogHeader><DialogTitle>{stockForm.mode === 'adjust' ? 'Adjust' : 'Add'} Stock: {showAddStock?.name}</DialogTitle><DialogDescription>Current quantity: {showAddStock?.quantity}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Button variant={stockForm.mode === 'add' ? 'default' : 'outline'} size="sm" onClick={() => setStockForm({ ...stockForm, mode: 'add', quantity: Math.abs(stockForm.quantity) || 0 })} data-testid="stock-mode-add">Add Stock</Button>
              <Button variant={stockForm.mode === 'adjust' ? 'default' : 'outline'} size="sm" onClick={() => setStockForm({ ...stockForm, mode: 'adjust' })} data-testid="stock-mode-adjust">Adjust (+/-)</Button>
            </div>
            <div className="space-y-2">
              <Label>{stockForm.mode === 'adjust' ? 'Adjustment (negative to reduce)' : 'Quantity to Add'} *</Label>
              <Input type="number" min={stockForm.mode === 'adjust' ? undefined : '1'} value={stockForm.quantity} onChange={(e) => setStockForm({ ...stockForm, quantity: parseInt(e.target.value) || 0 })} data-testid="stock-qty" />
              {stockForm.mode === 'adjust' && stockForm.quantity < 0 && (
                <p className="text-xs text-amber-600">This will reduce stock by {Math.abs(stockForm.quantity)} units (new total: {(showAddStock?.quantity || 0) + stockForm.quantity})</p>
              )}
            </div>
            <div className="space-y-2"><Label>Date</Label><Input type="date" value={stockForm.date_received} onChange={(e) => setStockForm({ ...stockForm, date_received: e.target.value })} data-testid="stock-date" /></div>
            <div className="space-y-2"><Label>Vendor / PO Reference</Label><Input value={stockForm.vendor_reference} onChange={(e) => setStockForm({ ...stockForm, vendor_reference: e.target.value })} placeholder="e.g., PO-12345 or Vendor name" data-testid="stock-vendor" /></div>
            <div className="space-y-2"><Label>Notes / Reason</Label><Textarea value={stockForm.notes} onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })} placeholder={stockForm.mode === 'adjust' ? 'Reason for adjustment (damage, loss, correction, etc.)' : 'Optional notes...'} data-testid="stock-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStock(null)}>Cancel</Button>
            <Button onClick={handleAddStock} disabled={saving} data-testid="confirm-add-stock-btn">{saving ? 'Saving...' : stockForm.mode === 'adjust' ? 'Apply Adjustment' : 'Add Stock'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== UNIFIED SEND DIALOG ===== */}
      <Dialog open={showSend} onOpenChange={(open) => { setShowSend(open); }}>
        <DialogContent className="max-w-2xl" data-testid="send-dialog">
          <DialogHeader>
            <DialogTitle>Send {sendForm.kit_name || 'Kit'}</DialogTitle>
            <DialogDescription>Choose send type and confirm details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            {/* Send Type Toggle */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
              <button className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${sendType === 'individual' ? 'bg-surface shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setSendType('individual')} data-testid="send-type-individual">Individual Send</button>
              <button className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${sendType === 'bulk' ? 'bg-surface shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setSendType('bulk')} data-testid="send-type-bulk">Bulk Send</button>
            </div>

            {/* Bulk: Kit Quantity */}
            {sendType === 'bulk' && (
              <div className="space-y-2">
                <Label>Number of Kits to Send *</Label>
                <Input type="number" min="1" value={sendForm.kit_quantity} onChange={(e) => setSendForm({ ...sendForm, kit_quantity: parseInt(e.target.value) || 1 })} data-testid="send-kit-qty" />
              </div>
            )}

            {/* Kit selector (if no kit pre-selected) */}
            {!sendForm.kit_template_id && (
              <div className="space-y-2">
                <Label>Kit Template</Label>
                <Select value={sendForm.kit_template_id || 'none'} onValueChange={(v) => loadKitIntoSend(v)}>
                  <SelectTrigger data-testid="send-kit-select"><SelectValue placeholder="Select a kit or add items manually" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ad hoc (manual)</SelectItem>
                    {kits.map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Items summary with deduction preview */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Items {sendType === 'bulk' ? 'per Kit' : 'to Send'}</Label>
                {!sendForm.kit_template_id && (
                  <Select onValueChange={(itemId) => { const item = items.find(i => i.id === itemId); if (item && !sendForm.items.find(si => si.item_id === itemId)) { setSendForm({ ...sendForm, items: [...sendForm.items, { item_id: itemId, item_name: item.name, quantity: 1 }] }); } }}>
                    <SelectTrigger className="w-[180px]" data-testid="send-add-item"><SelectValue placeholder="+ Add item" /></SelectTrigger>
                    <SelectContent>{items.filter(i => i.quantity > 0).map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.quantity})</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              {sendForm.items.length > 0 ? (
                <div className="border rounded-lg divide-y">
                  {sendForm.items.map((si, idx) => {
                    const srcItem = items.find(i => i.id === si.item_id);
                    const multiplier = sendType === 'bulk' ? sendForm.kit_quantity : 1;
                    const totalNeeded = si.quantity * multiplier;
                    const hasShortfall = srcItem && srcItem.quantity < totalNeeded;
                    const willBeLow = srcItem && !hasShortfall && (srcItem.quantity - totalNeeded) <= (srcItem?.low_stock_threshold || 0);
                    return (
                      <div key={si.item_id || `swag-row-${idx}`} className="flex items-center gap-3 p-3">
                        <span className="flex-1 text-sm font-medium">{si.item_name}</span>
                        {hasShortfall && <Badge variant="destructive" className="text-xs">Short by {totalNeeded - srcItem.quantity}</Badge>}
                        {willBeLow && <AlertTriangle className="w-4 h-4 text-amber-500" title="Will trigger low stock" />}
                        {sendType === 'bulk' && <span className="text-xs text-slate-500">per kit:</span>}
                        <Input type="number" min="1" max={srcItem?.quantity || 999} className="w-16" value={si.quantity} onChange={(e) => { const updated = [...sendForm.items]; updated[idx] = { ...updated[idx], quantity: parseInt(e.target.value) || 1 }; setSendForm({ ...sendForm, items: updated }); }} data-testid={`send-qty-${idx}`} />
                        {sendType === 'bulk' && <span className="text-xs font-medium text-slate-600">= {totalNeeded} total</span>}
                        <span className="text-xs text-slate-400">/ {srcItem?.quantity || 0}</span>
                        {!sendForm.kit_template_id && <Button variant="ghost" size="sm" onClick={() => setSendForm({ ...sendForm, items: sendForm.items.filter((_, i) => i !== idx) })}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-400 p-4 text-center border rounded-lg">No items added. Select a kit or add items above.</p>
              )}
            </div>

            {/* Form fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{sendType === 'individual' ? 'Recipient Name *' : 'Recipient (optional)'}</Label>
                <Input value={sendForm.recipient_name} onChange={(e) => setSendForm({ ...sendForm, recipient_name: e.target.value })} placeholder={sendType === 'individual' ? 'John Doe' : 'e.g., June Cohort'} data-testid="send-recipient" />
              </div>
              <div className="space-y-2"><Label>Event / Campaign</Label><Input value={sendForm.event_or_campaign} onChange={(e) => setSendForm({ ...sendForm, event_or_campaign: e.target.value })} placeholder="e.g., Job Fair 2026" data-testid="send-campaign" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date Sent</Label><Input type="date" value={sendForm.date_sent} onChange={(e) => setSendForm({ ...sendForm, date_sent: e.target.value })} data-testid="send-date" /></div>
              <div></div>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={sendForm.notes} onChange={(e) => setSendForm({ ...sendForm, notes: e.target.value })} placeholder="Optional notes..." data-testid="send-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSend(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={saving || sendForm.items.length === 0} data-testid="confirm-send-btn">
              {saving ? 'Processing...' : sendType === 'bulk' ? `Send ${sendForm.kit_quantity} Kit${sendForm.kit_quantity > 1 ? 's' : ''}` : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== KIT TEMPLATE DIALOG ===== */}
      <Dialog open={showKit} onOpenChange={(open) => { setShowKit(open); if (!open) setEditingKit(null); }}>
        <DialogContent className="max-w-lg" data-testid="kit-dialog">
          <DialogHeader><DialogTitle>{editingKit ? 'Edit Kit Template' : 'New Kit Template'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Kit Name *</Label><Input value={kitForm.name} onChange={(e) => setKitForm({ ...kitForm, name: e.target.value })} placeholder="e.g., Standard Welcome Bag" data-testid="kit-name" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={kitForm.description} onChange={(e) => setKitForm({ ...kitForm, description: e.target.value })} placeholder="What's this kit for?" data-testid="kit-desc" /></div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Kit Contents</Label>
                <Select onValueChange={(itemId) => { const item = items.find(i => i.id === itemId); if (item && !kitForm.items.find(ki => ki.item_id === itemId)) { setKitForm({ ...kitForm, items: [...kitForm.items, { item_id: itemId, item_name: item.name, quantity: 1 }] }); } }}>
                  <SelectTrigger className="w-[180px]" data-testid="kit-add-item"><SelectValue placeholder="+ Add item" /></SelectTrigger>
                  <SelectContent>{items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {kitForm.items.length > 0 ? (
                <div className="border rounded-lg divide-y">
                  {kitForm.items.map((ki, idx) => (
                    <div key={ki.item_id || `kit-row-${idx}`} className="flex items-center gap-3 p-3">
                      <span className="flex-1 text-sm">{ki.item_name}</span>
                      <Input type="number" min="1" className="w-16" value={ki.quantity} onChange={(e) => { const updated = [...kitForm.items]; updated[idx] = { ...updated[idx], quantity: parseInt(e.target.value) || 1 }; setKitForm({ ...kitForm, items: updated }); }} data-testid={`kit-qty-${idx}`} />
                      <Button variant="ghost" size="sm" onClick={() => setKitForm({ ...kitForm, items: kitForm.items.filter((_, i) => i !== idx) })}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400 p-4 text-center border rounded-lg">Add items to this kit</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKit(false)}>Cancel</Button>
            <Button onClick={handleSaveKit} disabled={saving} data-testid="save-kit-btn">{saving ? 'Saving...' : editingKit ? 'Update Kit' : 'Create Kit'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
